import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/contexts/auth'
import { captureError } from '@/lib/monitoring'
import { supabase } from '@/lib/supabase'
import type {
  ChallengeCategory,
  ChallengeEntry,
  ClubChallenge,
  ClubChallengeSummary,
  EntryComment,
} from '@/types'

const CHALLENGES_KEY = 'club-challenges'
const BOARD_KEY = 'challenge-board'
const COMMENTS_KEY = 'entry-comments'

function fail(error: unknown, context: string, extra?: Record<string, unknown>): never {
  captureError(error as Error, { context, ...extra })
  throw error
}

/** Challenges of a club, newest period first, with the current user's progress */
export function useClubChallenges(clubId: string | undefined) {
  const { user } = useAuth()

  return useQuery({
    queryKey: [CHALLENGES_KEY, clubId, user?.id],
    queryFn: async (): Promise<ClubChallengeSummary[]> => {
      const { data, error } = await supabase
        .from('club_challenges')
        .select('*, challenge_categories(count), challenge_entries(user_id, completed_on)')
        .eq('club_id', clubId)
        .order('starts_on', { ascending: false })
      if (error) fail(error, 'useClubChallenges', { clubId })

      return (data || []).map(({ challenge_categories, challenge_entries, ...challenge }) => ({
        ...(challenge as ClubChallenge),
        category_count: (challenge_categories as { count: number }[])?.[0]?.count ?? 0,
        my_completed: (
          (challenge_entries || []) as Pick<ChallengeEntry, 'user_id' | 'completed_on'>[]
        ).filter((e) => e.user_id === user?.id && e.completed_on).length,
      }))
    },
    enabled: !!user?.id && !!clubId,
  })
}

export function useChallenge(challengeId: string | undefined) {
  const { user } = useAuth()

  return useQuery({
    queryKey: [CHALLENGES_KEY, 'one', challengeId],
    queryFn: async (): Promise<ClubChallenge | null> => {
      const { data, error } = await supabase
        .from('club_challenges')
        .select('*')
        .eq('id', challengeId)
        .maybeSingle()
      if (error) fail(error, 'useChallenge', { challengeId })
      return data
    },
    enabled: !!user?.id && !!challengeId,
  })
}

export interface ChallengeBoard {
  categories: ChallengeCategory[]
  entries: ChallengeEntry[]
  commentCounts: Record<string, number>
}

/** Categories + everyone's entries + comment counts for a challenge */
export function useChallengeBoard(challengeId: string | undefined) {
  const { user } = useAuth()

  return useQuery({
    queryKey: [BOARD_KEY, challengeId],
    queryFn: async (): Promise<ChallengeBoard> => {
      const [categories, entries] = await Promise.all([
        supabase
          .from('challenge_categories')
          .select('*')
          .eq('challenge_id', challengeId)
          .order('position')
          .order('created_at'),
        supabase
          .from('challenge_entries')
          .select('*, entry_comments(count)')
          .eq('challenge_id', challengeId),
      ])
      if (categories.error) fail(categories.error, 'useChallengeBoard.categories')
      if (entries.error) fail(entries.error, 'useChallengeBoard.entries')

      const commentCounts: Record<string, number> = {}
      const cleanEntries = (entries.data || []).map(({ entry_comments, ...entry }) => {
        commentCounts[entry.id] = (entry_comments as { count: number }[])?.[0]?.count ?? 0
        return entry as ChallengeEntry
      })

      return { categories: categories.data || [], entries: cleanEntries, commentCounts }
    },
    enabled: !!user?.id && !!challengeId,
  })
}

type ChallengeInput = Pick<
  ClubChallenge,
  'name' | 'description' | 'starts_on' | 'ends_on' | 'target_categories'
>

export function useCreateChallenge() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async ({ clubId, ...input }: ChallengeInput & { clubId: string }) => {
      const { data, error } = await supabase
        .from('club_challenges')
        .insert({ ...input, club_id: clubId, created_by: user?.id })
        .select()
        .single()
      if (error) fail(error, 'useCreateChallenge', { clubId })
      return data as ClubChallenge
    },
    onSuccess: (challenge) =>
      queryClient.invalidateQueries({ queryKey: [CHALLENGES_KEY, challenge.club_id] }),
  })
}

export function useUpdateChallenge() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...input }: ChallengeInput & { id: string }) => {
      const { data, error } = await supabase
        .from('club_challenges')
        .update(input)
        .eq('id', id)
        .select()
        .single()
      if (error) fail(error, 'useUpdateChallenge', { id })
      return data as ClubChallenge
    },
    onSuccess: (challenge) => {
      queryClient.invalidateQueries({ queryKey: [CHALLENGES_KEY] })
      // Dates may change who completed what
      queryClient.invalidateQueries({ queryKey: [BOARD_KEY, challenge.id] })
    },
  })
}

export function useDeleteChallenge() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (challenge: ClubChallenge) => {
      const { error } = await supabase.from('club_challenges').delete().eq('id', challenge.id)
      if (error) fail(error, 'useDeleteChallenge', { id: challenge.id })
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CHALLENGES_KEY] }),
  })
}

function useBoardMutation<TVars>(
  context: string,
  run: (vars: TVars, userId: string) => PromiseLike<{ error: unknown }>,
  challengeIdOf: (vars: TVars) => string
) {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async (vars: TVars) => {
      if (!user?.id) throw new Error('errors.userNotAuthenticated')
      const { error } = await run(vars, user.id)
      if (error) fail(error, context)
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: [BOARD_KEY, challengeIdOf(vars)] })
      queryClient.invalidateQueries({ queryKey: [CHALLENGES_KEY] })
    },
  })
}

export function useAddCategory() {
  return useBoardMutation<{
    challengeId: string
    title: string
    description?: string
    position: number
  }>(
    'useAddCategory',
    ({ challengeId, title, description, position }, userId) =>
      supabase.from('challenge_categories').insert({
        challenge_id: challengeId,
        title: title.trim(),
        description: description?.trim() || null,
        suggested_by: userId,
        position,
      }),
    (v) => v.challengeId
  )
}

export function useUpdateCategory() {
  return useBoardMutation<{ category: ChallengeCategory; title: string; description?: string }>(
    'useUpdateCategory',
    ({ category, title, description }) =>
      supabase
        .from('challenge_categories')
        .update({ title: title.trim(), description: description?.trim() || null })
        .eq('id', category.id),
    (v) => v.category.challenge_id
  )
}

export function useDeleteCategory() {
  return useBoardMutation<ChallengeCategory>(
    'useDeleteCategory',
    (category) => supabase.from('challenge_categories').delete().eq('id', category.id),
    (category) => category.challenge_id
  )
}

/** Sets (or replaces) the current user's book for a category */
export function useSetEntry() {
  return useBoardMutation<{ challengeId: string; categoryId: string; bookId: string }>(
    'useSetEntry',
    ({ challengeId, categoryId, bookId }, userId) =>
      supabase.from('challenge_entries').upsert(
        {
          challenge_id: challengeId,
          category_id: categoryId,
          user_id: userId,
          book_id: bookId,
          // Filled by the database trigger from the book
          book_title: '',
        },
        { onConflict: 'category_id,user_id' }
      ),
    (v) => v.challengeId
  )
}

export function useRemoveEntry() {
  return useBoardMutation<ChallengeEntry>(
    'useRemoveEntry',
    (entry) => supabase.from('challenge_entries').delete().eq('id', entry.id),
    (entry) => entry.challenge_id
  )
}

/** Refetch a board after something outside it changed (e.g. a book marked as read) */
export function useRefreshBoard() {
  const queryClient = useQueryClient()
  return (challengeId: string) => {
    queryClient.invalidateQueries({ queryKey: [BOARD_KEY, challengeId] })
    queryClient.invalidateQueries({ queryKey: [CHALLENGES_KEY] })
  }
}

export function useEntryComments(entryId: string | undefined) {
  const { user } = useAuth()

  return useQuery({
    queryKey: [COMMENTS_KEY, entryId],
    queryFn: async (): Promise<EntryComment[]> => {
      const { data, error } = await supabase
        .from('entry_comments')
        .select('*')
        .eq('entry_id', entryId)
        .order('created_at')
      if (error) fail(error, 'useEntryComments', { entryId })
      return data || []
    },
    enabled: !!user?.id && !!entryId,
  })
}

export function useAddComment() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async ({ entry, body }: { entry: ChallengeEntry; body: string }) => {
      const { error } = await supabase
        .from('entry_comments')
        .insert({ entry_id: entry.id, user_id: user?.id, body: body.trim() })
      if (error) fail(error, 'useAddComment', { entryId: entry.id })
    },
    onSuccess: (_data, { entry }) => {
      queryClient.invalidateQueries({ queryKey: [COMMENTS_KEY, entry.id] })
      queryClient.invalidateQueries({ queryKey: [BOARD_KEY, entry.challenge_id] })
    },
  })
}

export function useDeleteComment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ comment }: { comment: EntryComment; challengeId: string }) => {
      const { error } = await supabase.from('entry_comments').delete().eq('id', comment.id)
      if (error) fail(error, 'useDeleteComment', { id: comment.id })
    },
    onSuccess: (_data, { comment, challengeId }) => {
      queryClient.invalidateQueries({ queryKey: [COMMENTS_KEY, comment.entry_id] })
      queryClient.invalidateQueries({ queryKey: [BOARD_KEY, challengeId] })
    },
  })
}
